import { useGlobalStore } from '@/stores';
import { BusinessPartnerScope } from '@/api/roles/roles.types';
import { AccountType } from '@/constants/account-type';

/**
 * Current user's effective permissions and data scope.
 *
 * `isAdmin` is derived on the backend from the assigned `Administrator`
 * system role — never recomputed on the frontend. Admins implicitly hold
 * every permission, so `has(...)` short-circuits to true for them.
 */
export function usePermissions() {
  const permissions = useGlobalStore((state) => state.permissions);
  const accountType = useGlobalStore((state) => state.accountType);
  const isAdmin = useGlobalStore((state) => state.isAdmin);
  const businessPartnerScope = useGlobalStore(
    (state) => state.businessPartnerScope,
  );
  const salesPersonCode = useGlobalStore((state) => state.salesPersonCode);

  const has = (permission?: string | null): boolean =>
    !permission || isAdmin || permissions.includes(permission);

  const hasAny = (required: string[]): boolean =>
    required.length === 0 ||
    isAdmin ||
    required.some((p) => permissions.includes(p));

  return {
    permissions,
    accountType: accountType as AccountType,
    isAdmin,
    businessPartnerScope: businessPartnerScope as BusinessPartnerScope,
    salesPersonCode,
    has,
    hasAny,
  };
}

export function useHasPermission(permission?: string | null): boolean {
  return usePermissions().has(permission);
}
